/**
 * Session message origin kinds — dialogue vs control-plane separation.
 *
 * Product rule: the user must never be confused about what is "in" a session.
 * JSONL remains the dialogue source of truth; control traffic is tagged at write
 * time and stripped from model history / compaction / chat UI.
 */

import { isHeartbeatPromptText } from "../auto-reply/heartbeat.js";

/** Durable field persisted on AgentMessage JSON (extra key; parsers tolerate it). */
export const MESSAGE_ORIGIN_FIELD = "originKind" as const;

/** Keep in sync with chat/transcript-filters CONVERSATION_SUMMARY_HEADER. */
const CONVERSATION_SUMMARY_HEADER = "[Conversation summary from older dropped turns]";

export type MessageOriginKind =
  | "dialogue"
  | "heartbeat"
  | "fleet_announce"
  | "inject"
  | "compaction_summary"
  | "system";

const CONTROL_PLANE = new Set<MessageOriginKind>([
  "heartbeat",
  "fleet_announce",
  "inject",
  "compaction_summary",
  "system",
]);

function messageText(message: unknown): string {
  if (!message || typeof message !== "object") {
    return "";
  }
  const m = message as Record<string, unknown>;
  if (typeof m.content === "string") {
    return m.content;
  }
  if (Array.isArray(m.content)) {
    return m.content
      .map((item) => {
        const x = item as Record<string, unknown>;
        return typeof x.text === "string" ? x.text : "";
      })
      .join("\n");
  }
  if (typeof m.text === "string") {
    return m.text;
  }
  return "";
}

export function isDialogueOrigin(kind: MessageOriginKind | undefined | null): boolean {
  return kind === "dialogue" || kind == null;
}

export function isControlPlaneOrigin(kind: MessageOriginKind | undefined | null): boolean {
  return kind != null && CONTROL_PLANE.has(kind);
}

export function getMessageOriginKind(message: unknown): MessageOriginKind | undefined {
  if (!message || typeof message !== "object") {
    return undefined;
  }
  const raw = (message as Record<string, unknown>)[MESSAGE_ORIGIN_FIELD];
  if (typeof raw !== "string") {
    return undefined;
  }
  switch (raw) {
    case "dialogue":
    case "heartbeat":
    case "fleet_announce":
    case "inject":
    case "compaction_summary":
    case "system":
      return raw;
    default:
      return undefined;
  }
}

export function tagMessageOrigin<T>(message: T, kind: MessageOriginKind): T {
  if (!message || typeof message !== "object") {
    return message;
  }
  return { ...(message as object), [MESSAGE_ORIGIN_FIELD]: kind } as T;
}

/**
 * Infer origin for untagged messages (legacy transcripts + content heuristics).
 */
export function inferMessageOriginKind(message: unknown): MessageOriginKind {
  const explicit = getMessageOriginKind(message);
  if (explicit) {
    return explicit;
  }
  if (!message || typeof message !== "object") {
    return "dialogue";
  }
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  const stopReason = typeof m.stopReason === "string" ? m.stopReason : "";
  const text = messageText(message);

  if (stopReason === "injected") {
    if (isFleetAnnounceText(text)) {
      return "fleet_announce";
    }
    return "inject";
  }

  if (role === "user") {
    if (isHeartbeatPromptText(text)) {
      return "heartbeat";
    }
    if (isFleetAnnounceText(text)) {
      return "fleet_announce";
    }
    if (text.trimStart().startsWith(CONVERSATION_SUMMARY_HEADER)) {
      const after = text.replace(
        /^\[Conversation summary from older dropped turns\][\s\S]*?\[End summary\](?:\r?\n)*/,
        "",
      );
      if (!after.trim()) {
        return "compaction_summary";
      }
    }
  }

  if (role === "assistant" && isFleetAnnounceText(text)) {
    return "fleet_announce";
  }

  return "dialogue";
}

export function resolveMessageOriginKind(message: unknown): MessageOriginKind {
  return inferMessageOriginKind(message);
}

export function isControlPlaneMessage(message: unknown): boolean {
  return isControlPlaneOrigin(resolveMessageOriginKind(message));
}

export function isDialogueMessage(message: unknown): boolean {
  return !isControlPlaneMessage(message);
}

export function isFleetAnnounceText(text: string): boolean {
  const t = text.trim();
  if (!t) {
    return false;
  }
  if (/^A background task\b/i.test(t)) {
    return true;
  }
  if (/^\[subagent-result\]/i.test(t)) {
    return true;
  }
  if (/just completed successfully/i.test(t) && /background task/i.test(t)) {
    return true;
  }
  return false;
}

/**
 * Stamp originKind when missing (write-time hygiene). Does not overwrite explicit tags.
 */
export function ensureMessageOrigin(message: unknown): unknown {
  if (!message || typeof message !== "object") {
    return message;
  }
  if (getMessageOriginKind(message)) {
    return message;
  }
  return tagMessageOrigin(message, inferMessageOriginKind(message));
}

/**
 * Heuristic: shell/log pastes should not auto-spawn Fleet Work Plans.
 */
export function looksLikeTerminalOrLogPaste(message: string): boolean {
  const text = message.trim();
  if (!text) {
    return false;
  }
  if (isHeartbeatPromptText(text)) {
    return true;
  }
  if (/REMOTE HOST IDENTIFICATION HAS CHANGED/i.test(text)) {
    return true;
  }
  if (/Host key verification failed/i.test(text)) {
    return true;
  }
  if (/^\s*From https?:\/\//m.test(text) && /\*\s*\[new branch\]/.test(text)) {
    return true;
  }
  if (/\[[\w.-]+@[\w.-]+\s+[^\]]+\]\$/.test(text)) {
    return true;
  }
  if (/^\[piotro@|^\[root@/m.test(text)) {
    return true;
  }
  const lines = text.split("\n").filter((l) => l.trim());
  if (lines.length >= 8) {
    const listing = lines.filter(
      (l) => /^[-dlrwxs]{9,}/.test(l.trim()) || /->\s+\//.test(l),
    ).length;
    if (listing >= 3 && !/\b(please|fix|implement|investigate|analyze)\b/i.test(text)) {
      return true;
    }
  }
  return false;
}
